import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Server } from 'lucide-react';
import api from '../../utils/api';
import { statusColor, pctColor } from '../../components/VpsVisuals';

export const meta = {
  id: 'vps_health',
  category: 'system',
  titleKey: 'dashboard.widgets.vpsHealth',
  descriptionKey: 'dashboard.widgets.vpsHealthDesc',
  defaultSize: 'md',
  requiresModule: 'vps_health',
  icon: Server,
};

function MiniBar({ label, pct, warn, crit }) {
  const v = pct == null || isNaN(pct) ? null : Math.max(0, Math.min(100, pct));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <span style={{ width: 30, color: 'var(--c-text-muted)' }}>{label}</span>
      <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--c-surface-2)', overflow: 'hidden' }}>
        <div style={{ width: `${v ?? 0}%`, height: '100%', background: pctColor(v, warn, crit), transition: 'width 0.5s' }} />
      </div>
      <span style={{ width: 30, textAlign: 'right', color: 'var(--c-text)' }}>{v == null ? '—' : `${Math.round(v)}%`}</span>
    </div>
  );
}

export default function VpsHealthWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['vps-health-overview'],
    queryFn: () => api.get('/vps-health/overview').then(r => r.data),
    refetchInterval: 60 * 1000,
  });

  const hosts = data?.hosts || [];
  const counts = data?.counts || { total: 0 };
  const agg = data?.aggregate || {};

  return (
    <div className="card" style={{ cursor: 'pointer', height: '100%' }} onClick={() => navigate('/vps-health')}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Server size={16} /> {t('dashboard.widgets.vpsHealth')}
        </div>
        {agg.uptime_pct != null && (
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
            {agg.uptime_pct}% {t('vpsHealth.uptime')}
          </span>
        )}
      </div>
      {hosts.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      ) : (
        <div style={{ padding: '6px 16px 14px' }}>
          {/* status pills */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12 }}>
            <Pill color={statusColor('ok')} n={counts.ok} label={t('vpsHealth.status.ok')} />
            {counts.warning > 0 && <Pill color={statusColor('warning')} n={counts.warning} label={t('vpsHealth.status.warning')} />}
            {counts.critical > 0 && <Pill color={statusColor('critical')} n={counts.critical} label={t('vpsHealth.status.critical')} />}
            {counts.offline > 0 && <Pill color={statusColor('offline')} n={counts.offline} label={t('vpsHealth.status.offline')} />}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {hosts.slice(0, 4).map((h) => {
              const m = h.last_metrics || {};
              const memPct = m.mem_total_mb ? (m.mem_used_mb / m.mem_total_mb) * 100 : null;
              const cpuPct = h.cpu_cores && m.cpu_load_1 != null ? (m.cpu_load_1 / h.cpu_cores) * 100 : null;
              const diskPct = m.disk_percent != null ? Number(m.disk_percent) : null;
              return (
                <div key={h.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(h.status), flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{h.provider || ''}</span>
                  </div>
                  {h.status === 'offline' ? (
                    <div style={{ fontSize: 11, color: statusColor('offline') }}>{t('vpsHealth.status.offline')}</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <MiniBar label={t('vpsHealth.cpu')} pct={cpuPct} warn={Number(h.cpu_warn) || 150} crit={Number(h.cpu_crit) || 300} />
                      <MiniBar label={t('vpsHealth.ram')} pct={memPct} warn={Number(h.mem_warn) || 80} crit={Number(h.mem_crit) || 92} />
                      <MiniBar label={t('vpsHealth.disk')} pct={diskPct} warn={Number(h.disk_warn) || 85} crit={Number(h.disk_crit) || 92} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {hosts.length > 4 && (
            <div style={{ fontSize: 11, color: 'var(--c-brand)', marginTop: 8, textAlign: 'center' }}>
              +{hosts.length - 4} {t('vpsHealth.services')} →
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Pill({ color, n, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
      <span style={{ fontWeight: 700, color }}>{n}</span>
      <span style={{ color: 'var(--c-text-muted)' }}>{label}</span>
    </span>
  );
}
